import { ContentProvider } from "../../services/content/contentProvider";
import { DiscountNotFound } from "../../src/components/DiscountNotFound";
import { useRouter } from "next/router";
import { Box, Container } from "@mui/material";
import { DiscountType } from "../../src/types/discountTypes";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../api_config/firebase-configuration";
import { useUserData } from "../../src/hooks/useUserData";
import { useCallback, useContext, useEffect, useState } from "react";
import { UpgradePopup } from "../../src/components/CommonComponents/UpgradePopup";
import { LocalizationContext, ShareLinkObject } from "../../src/lib/context";
import { Interval } from "date-fns";
import differenceInDays from "date-fns/differenceInDays";
import formatDuration from "date-fns/formatDuration";
import intervalToDuration from "date-fns/intervalToDuration";
import { useScreenSize } from "../../src/hooks/useScreenSize";
import enLocale from "date-fns/locale/en-GB";
import { dcContent } from "services/content";
import PromotionalCard from "../../src/components/CommonComponents/PromotionalCard";
import SmallDiscountContent from "src/components/Discounts/SmallDiscountContent";
import LargeDiscountContent from "src/components/Discounts/LargeDiscountContent";
import { DbProvider } from "../../services/db/DbProvider";
import { NextSeo } from "next-seo";
import { GetStaticPaths } from "next";
import BottomInfo from "src/components/BottomInfo";

export const getStaticPaths: GetStaticPaths = async () => {
  const contentProvider = new ContentProvider("discounts");
  const discountsMetaQueryName = "_allDiscountsMeta";
  const discountsQueryName = "allDiscounts";
  const discountsMetaQuery = contentProvider.generateQuery(
    discountsMetaQueryName,
    [{ fields: ["count"] }]
  );
  const discountsQuery = (skip: number, first: number) => {
    return contentProvider.generateQuery(
      discountsQueryName,
      [{ fields: ["id"] }],
      {
        skip: skip,
        first: first,
        filter: `{remainingAmount: {gt: "0"},
                endDate: {gte: "${new Date().toString()}"}}`,
      }
    );
  };
  const discountsCount = (
    await contentProvider.request(discountsMetaQuery, discountsMetaQueryName)
  ).count;
  const allDiscounts: DiscountType[][] = [];
  for (let i = 0; i < discountsCount; i += 100) {
    let discounts: DiscountType[];
    try {
      discounts = await contentProvider.request(
        discountsQuery(i, 100),
        discountsQueryName
      );
      allDiscounts.push(discounts);
    } catch (e: any) {
      console.error(e);
    }
  }
  const paths = allDiscounts?.flat().map((discount: { id: string }) => ({
    params: { discount: discount?.id },
  })) || [{ params: { discount: "" } }];
  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({
  params,
  locale,
  defaultLocale,
}: {
  params: { discount: string };
  locale: string;
  defaultLocale: string;
}) => {
  const contentProvider = new ContentProvider("discounts");
  const discountQueryName = "discount";
  const discountQuery = contentProvider.generateQuery(
    discountQueryName,
    [
      {
        fields: [
          "id",
          "language",
          "endDate",
          "startDate",
          {
            name: "images",
            fields: ["url", "alt", "customData"],
          },
          {
            name: "logo",
            fields: ["url", "alt"],
          },
          "priceDiscGross",
          "discount",
          "domain",
          "priceOrigGross",
          "priceOrigNet",
          "currency",
          "description",
          "shortName",
          "prodOrServ",
          "country",
          "googleRating",
          "compName",
          "subtitle1",
          "subtitle2",
          "remainingAmount",
          "accessUrl",
          "application",
        ],
      },
    ],
    {
      filter: `{id: {eq: "${params.discount}"},
                remainingAmount: {gt: "0"},
                endDate: {gte: "${new Date().toString()}"}}`,
      locale: locale,
      fallbackLocales: defaultLocale,
    }
  );
  let discount: DiscountType;
  try {
    discount = await contentProvider.request(discountQuery, discountQueryName);
  } catch (e) {
    return { props: { discount: null } };
  }
  if (discount === undefined) {
    return { props: { discount: null } };
  }
  if (discount) {
    for (const img of discount?.images) {
      if (
        img.customData.rows === undefined &&
        img.customData.cols === undefined
      ) {
        img.customData.rows = "1";
        img.customData.cols = "1";
      }
    }
  }
  return {
    props: { discount },
    revalidate: 18000,
  };
};

export default function Discount({ discount }: { discount: DiscountType }) {
  const { user, actDroner } = useUserData();
  const currentMonth = `${new Date().getFullYear()}-${
    new Date().getMonth() + 1
  }`;
  const [numCouponsFromDb, setNumCouponsFromDb] = useState(
    discount?.remainingAmount
  );
  const [canGetCodes, setCanGetCodes] = useState(false);
  const { isFallback, locale } = useRouter();
  const [dateLocale, setDateLocale] = useState(enLocale);
  const [loading, setLoading] = useState(false);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const sendCodesFn = httpsCallable(functions, "discounts-sendDiscountCodes");
  const smAndDown = useScreenSize("sm", "down");
  const { localeStrings } = useContext(LocalizationContext);
  const promoCardStrings = localeStrings.uiGeneral.promoCards;
  const jobsPromoStrings = promoCardStrings.filter(
    (c) => c.promoId === "jobs"
  )[0];
  const joinPromoStrings = promoCardStrings.filter(
    (c) => c.promoId === "join"
  )[0];
  const bottomInfoTexts = [
    localeStrings.discountsPage.aboutBody1,
    localeStrings.discountsPage.aboutBody2,
    localeStrings.discountsPage.aboutBody3,
    localeStrings.discountsPage.aboutBody4,
  ];
  const { shareLinkObject, setShareLinkObject } = useContext(ShareLinkObject);
  const [buttonText, setButtonText] = useState(
    localeStrings.discountPage.loginAlert
  );
  const [tempTimeLeftString, setTempTimeLeftString] = useState("");
  const calculateTimeLeft = useCallback(() => {
    const timeToEnd =
      new Date(discount?.endDate).getTime() - new Date().getTime();
    let remainingHours;
    let remainingTime: Duration | undefined;
    if (timeToEnd >= 0) {
      try {
        const intervalToEnd: Interval = {
          start: new Date(),
          end: new Date(discount?.endDate),
        };
        remainingTime = intervalToDuration(intervalToEnd);
        remainingHours = differenceInDays(
          new Date(discount?.endDate),
          new Date()
        );
      } catch (e) {
        console.error(e);
      }
    }
    let timeLeftString = "";
    if (remainingTime === undefined || remainingHours === undefined) {
      setTempTimeLeftString("end of promotion");
    } else {
      timeLeftString += `${remainingHours} ${
        remainingHours > 1
          ? localeStrings.discountPage?.days
          : localeStrings.discountPage?.day
      } `;
      timeLeftString += formatDuration(remainingTime, {
        format: ["hours", "minutes", "seconds"],
        zero: true,
        locale: dateLocale,
      });
      setTempTimeLeftString(timeLeftString);
    }
  }, [
    dateLocale,
    discount?.endDate,
    localeStrings.discountPage?.day,
    localeStrings.discountPage?.days,
  ]);
  const [timeLeftString, setTimeLeftString] = useState("");

  useEffect(() => {
    const db = new DbProvider();
    const getRemainingAmount = async () => {
      const discountDocSnap = await db.getDocWithId("discounts", discount?.id);
      if (discountDocSnap && discountDocSnap.exists()) {
        return discountDocSnap.data();
      } else {
        return null;
      }
    };
    getRemainingAmount()
      .then((data) => {
        setNumCouponsFromDb(
          data && data.numOfCoupons
            ? data.numOfCoupons
            : discount?.remainingAmount
        );
      })
      .catch((e: any) => {
        new Error(e);
      });
  }, [discount]);

  useEffect(() => {
    if (actDroner) {
      if (!actDroner.discounts) {
        setCanGetCodes(true);
        setButtonText(localeStrings.discountPage.getMyCode);
      } else {
        if (
          actDroner.discounts.currentMonth === currentMonth &&
          actDroner.discounts.discountsInMonth >= actDroner.discountsPerMonth
        ) {
          setCanGetCodes(false);
          setButtonText(localeStrings.discountPage.offerEnded);
        } else if (actDroner.discounts.usedDiscounts?.includes(discount?.id)) {
          setCanGetCodes(false);
          setButtonText(localeStrings.discountPage.alreadyUsed);
        } else {
          setCanGetCodes(true);
          setButtonText(localeStrings.discountPage.getMyCode);
        }
      }
    }
  }, [
    actDroner,
    canGetCodes,
    currentMonth,
    discount?.id,
    localeStrings.discountPage.alreadyUsed,
    localeStrings.discountPage.getMyCode,
    localeStrings.discountPage.offerEnded,
  ]);

  useEffect(() => {
    setShareLinkObject({
      title: discount?.shortName,
      url: window.location.href,
    });
  }, [discount?.shortName, setShareLinkObject]);

  useEffect(() => {
    const importLocaleFile = async (dateFnLocaleCode: string) => {
      const localeToSet = await import(
        `date-fns/locale/${dateFnLocaleCode}/index.js`
      );
      setDateLocale(localeToSet.default);
    };
    if (locale !== dateLocale.code) {
      const dateFnLocaleCode = dcContent.routerLocaleToDateFn.filter(
        (l) => l.locale === locale
      )[0].dateFnLocale;
      importLocaleFile(dateFnLocaleCode).catch((e) => {
        console.error(e);
      });
    }
  }, [dateLocale.code, locale]);

  const handleSendCodes = async () => {
    if (
      !actDroner ||
      !(
        actDroner.subscriptionLevel === "játsszunk 3d-ben!" ||
        actDroner.subscriptionLevel === "enterprise" ||
        actDroner.subscriptionLevel === "standard"
      )
    ) {
      return setUpgradePopupOpen(true);
    } else {
      setLoading(true);
      try {
        const resp: any = await sendCodesFn({
          userId: actDroner?.uid,
          discountId: discount?.id,
          clientEmail: actDroner?.contactEmail ?? actDroner?.email,
          name: actDroner?.displayName ?? actDroner?.username,
          subLevel: actDroner?.subscriptionLevel,
        });
        if (resp.data && resp.data.status) {
          if (resp.data.status === "success") {
            setButtonText(localeStrings.discountPage.emailSent);
          } else if (resp.data.status === "error") {
            alert(resp.data.details);
          }
        }
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    }
  };

  if (isFallback) {
    return <div>{localeStrings.uiGeneral.loadingText}</div>;
  }
  if (!discount) {
    return <DiscountNotFound />;
  } else {
    useEffect(() => {
      const timeout = setTimeout(() => {
        calculateTimeLeft();
        setTimeLeftString(tempTimeLeftString);
      }, 1000);
      return () => {
        clearTimeout(timeout);
      };
    }, [calculateTimeLeft, tempTimeLeftString]);

    return (
      <>
        <NextSeo
          title={discount?.shortName}
          description={discount?.description}
          openGraph={{
            type: "website",
            title: discount?.shortName,
            description: discount?.description,
            images: [
              {
                url: discount?.images[0].url,
                alt: discount?.shortName,
              },
            ],
          }}
          twitter={{
            handle: "@handle",
            site: "@site",
            cardType: "summary_large_image",
          }}
        />
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
            py: 10,
            maxWidth: "1248px !important",
          }}
        >
          {!smAndDown && (
            <LargeDiscountContent
              discount={{ ...discount, numCouponsFromDb: numCouponsFromDb }}
              shareLinkObject={shareLinkObject}
              loading={loading}
              localeStrings={localeStrings}
              buttonText={buttonText}
              timeLeftString={timeLeftString}
              handleSendCodes={handleSendCodes}
              smAndDown={smAndDown}
              canGetCodes={canGetCodes}
            />
          )}
          {smAndDown && (
            <SmallDiscountContent
              discount={{ ...discount, numCouponsFromDb: numCouponsFromDb }}
              shareLinkObject={shareLinkObject}
              loading={loading}
              localeStrings={localeStrings}
              buttonText={buttonText}
              timeLeftString={timeLeftString}
              handleSendCodes={handleSendCodes}
              smAndDown={smAndDown}
              canGetCodes={canGetCodes}
            />
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <UpgradePopup
              setOpen={setUpgradePopupOpen}
              open={upgradePopupOpen}
              text={localeStrings.discountPage.upgradeModalText}
            />
          </Box>
          <div className="flex col gap-huge">
            <PromotionalCard
              title={jobsPromoStrings.title}
              content={jobsPromoStrings.description}
              btnContent={jobsPromoStrings.buttonText}
              imgUrl="/assets/jobs_cover_img.webp"
              imgHeight={147}
              dark
              left
              link="/jobs"
            />

            {!user && (
              <PromotionalCard
                title={joinPromoStrings.title}
                content={joinPromoStrings.description}
                imgUrl="/assets/unsplash.jpeg"
                btnContent={joinPromoStrings.buttonText}
                dark
                imgHeight={200}
                link="/register"
              />
            )}
          </div>
        </Container>

        <BottomInfo
          headerText={localeStrings.discountsPage.aboutTitle}
          textsArray={bottomInfoTexts}
          sendEmail={true}
          email={localeStrings?.errorPage?.mailToLink}
        />
      </>
    );
  }
}
